<template>
  <div
    class="draggable-container"
    @pointermove="updateMousePosition"
  >
    <DraggableElementsMenu v-if="activeElement">
      <TagEditMenu
        v-if="activeElementType === 'tag'"
        v-model="activeElement"
        @remove="removeTag"
      />
      <MarkerEditMenu
        v-if="activeElementType === 'marker'"
        v-model="activeElement"
      />
      <PolygonEditMenu
        v-if="activeElementType === 'polygon'"
        v-model="activeElement"
      />
    </DraggableElementsMenu>

    <div v-if="activeElementType === 'marker'" class="dot-status">
      x: <input class="form-control" type="number" v-model="activeElement.marker.x"> y: <input class="form-control" type="number" v-model="activeElement.marker.y">
    </div>
    <div v-if="activeElementType === 'tag'" class="dot-status">
      x: <input class="form-control" type="number" v-model="activeElement.pos.x"> y: <input class="form-control" type="number" v-model="activeElement.pos.y">
    </div>

    <LivingOptionTags
      v-model="tags"
      :active-element="activeElement"
      @selectedElement="selectedElement"
    />

    <LivingOptionMarkers
      :property-ids="propertyIds ? propertyIds : []"
      :subsection-ids="subsectionIds ? subsectionIds : []"
      :living-option-ids="livingOptionIds ? livingOptionIds : []"
      :active-element="activeElement"
      @selectedElement="selectedElement"
    />
    
    <LivingOptionPolygons
      :property-ids="propertyIds ? propertyIds : []"
      :subsection-ids="subsectionIds ? subsectionIds : []"
      :living-option-ids="livingOptionIds ? livingOptionIds : []"
      :active-element="activeElement"
      :active-element-type="activeElementType"
      @selectedElement="selectedElement"
    />

    <!-- Background image -->
    <div
      class="draggable-container__bg"
      :class="{'no-image' : !backgroundImage }"
      @click="clearSelection"
    >
      <img
        v-if="assetType(backgroundImage) === 'image'"
        class="tag-bg"
        :class="{'hidden' : backgroundImage === ''}"
        :src="$store.state.assetsPath + backgroundImage"
      >
      <video
        v-if="assetType(backgroundImage) === 'video'"
        class="tag-bg"
        :class="{'hidden' : backgroundImage === ''}"
        :src="$store.state.assetsPath + backgroundImage"
        autoplay
        muted
        loop
      ></video>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex'
import DraggableElementsMenu from '@/components/DraggableElementsMenu'
import LivingOptionTags from '@/components/LivingOptionTags'
import LivingOptionMarkers from '@/components/LivingOptionMarkers'
import LivingOptionPolygons from '@/components/LivingOptionPolygons'
import TagEditMenu from '@/components/TagEditMenu'
import MarkerEditMenu from '@/components/MarkerEditMenu'
import PolygonEditMenu from '@/components/PolygonEditMenu'

export default {
  props:['tags', 'propertyIds', 'subsectionIds', 'livingOptionIds', 'backgroundImage'],

  components: {
    DraggableElementsMenu,
    LivingOptionTags,
    LivingOptionMarkers,
    LivingOptionPolygons,
    TagEditMenu,
    MarkerEditMenu,
    PolygonEditMenu,
  },

  data () {
    return {
      mousePosition: {
        x: 0,
        y: 0,
      },
      
      mousePositionPolygonPointOffset: {
        x: 0,
        y: 0,
      },

      zoomPosition: {
        x: 0,
        y: 0,
      },

      activeElement: null,
      activeElementType: '',
      showPolygonControls: true
    }
  },

  computed: {
    ...mapGetters([
      'activeLanguage',
    ]),

    ...mapState({
      livingOptions: state => state.living.livingOptions,
      subsections: state => state.living.subsections,
      properties: state => state.living.properties,
      configItems: state => state.config.configItems
    })
  },

  methods: {
    assetType (asset) {
      if (asset) {
        const assetExt = asset.split('.').pop()
        const videoTypes = [
          'mp4',
          'mov',
        ]

        if (videoTypes.includes(assetExt)) {
          return 'video'
        } else {
          return 'image'
        }
      }

      return ''
    },

    updateMousePosition (e) {
      this.mousePosition.x = e.offsetX
      this.mousePosition.y = e.offsetY

      // get markers-wrapper size and position
      let markersWrapper = document.querySelector('.draggable-container')
      let markersWrapperWidth = markersWrapper.getBoundingClientRect().width
      let markersWrapperHeight = markersWrapper.getBoundingClientRect().height
      let markersWrapperX = markersWrapper.getBoundingClientRect().x
      let markersWrapperY = markersWrapper.getBoundingClientRect().y

      // get mouse screen position
      let mouseXRelative = e.clientX - markersWrapperX
      let mouseYRelative = e.clientY - markersWrapperY

      // get mouse position in percentage
      let mouseX = - mouseXRelative / markersWrapperWidth * 100
      let mouseY = - mouseYRelative / markersWrapperHeight * 100

      this.mousePosition = {
        x: mouseX,
        y: mouseY
      }

      this.zoomPosition = {
        x: mouseXRelative - markersWrapperX + 100,
        y: mouseYRelative - 100
      }
    },

    selectedElement (element, type) {
      this.activeElement = element
      this.activeElementType = type
    },

    clearSelection () {
      this.activeElement = null
      this.activeElementType = ''
    },

    removeTag (tagId) {
      const parentId = this.$route.params.id
      const parent = [...this.livingOptions, ...this.subsections, ...this.properties].find(element => {
        if (element._id === parentId) {
          return element
        }
      })

      if (parent) {
        parent.tags = parent.tags.filter(tag => tag._id !== tagId)
      } else {
        const masterplanTags = this.configItems.find(item => item.key === 'liveHereMasterplanTags')
        masterplanTags.value = masterplanTags.value.filter(tag => tag._id !== tagId)
      }

      this.clearSelection()
    }
  }
}
</script>

<style lang="scss" scoped>
.draggable-container {
  aspect-ratio: 3190 / 1960;
  overflow: hidden;
  pointer-events: all;
  position: relative;
  z-index: 1000;

  &__bg {
    height: 100%;
    pointer-events: all;
    position: relative;

    img {
      height: 100%;
      object-fit: cover;
      width: 100%;
    }
  }
}

.no-image {
  background-image: linear-gradient(45deg, #eee 25%, transparent 25%, transparent 75%, #eee 75%, #eee 100%), linear-gradient(45deg, #eee 25%, white 25%, white 75%, #eee 75%, #eee 100%);
  background-position: 0px 0px, 10px 10px;
  background-size: 20px 20px;
  }

.tag-bg {
  height: 100%;
  object-fit: cover;
  pointer-events: none;
  width: 100%;

  &.hidden {
    opacity: 0;
  }
}

.dot-status {
  background-color: rgba(#fff, 0.7);
  color: #000;
  font-size: 16px;
  padding: 10px;
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  z-index: 6;

  input.form-control  {
    display: inline-block;
    padding: 0 5px;
    width: 60px;
  }
}
</style>
